/**
 * @ipc/validation · src/schemas/catalog.ts — поиск ИКПУ (МХИК) и кодов упаковки
 * в официальном реестре tasnif.soliq.uz (ГНК Узбекистана).
 *
 * Query-параметры API `GET /api/v1/catalog/ikpu/*` и формы admin-ui.
 */
import { z } from 'zod/v4';

/** Язык ответов tasnif.soliq.uz. */
export const ikpuLangSchema = z.enum(['ru', 'uz', 'en']).default('ru');
export type IkpuLang = z.infer<typeof ikpuLangSchema>;

/** GET /api/v1/catalog/ikpu/search */
export const ikpuSearchQuerySchema = z.object({
  q: z
    .string()
    .trim()
    .min(2, 'Введите не менее 2 символов для поиска')
    .max(200, 'Запрос: не более 200 символов'),
  lang: ikpuLangSchema,
  page: z.coerce.number().int().min(0).default(0),
  size: z.coerce.number().int().min(1).max(50).default(20),
});
export type IkpuSearchQueryDto = z.infer<typeof ikpuSearchQuerySchema>;

/** Код ИКПУ (mxik) — 17 цифр по каталогу tasnif. */
export const ikpuCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{10,20}$/, 'ИКПУ: ожидается числовой код из реестра tasnif.soliq.uz');
export type IkpuCode = z.infer<typeof ikpuCodeSchema>;

/** Элемент результата поиска. */
export const ikpuSearchItemSchema = z.object({
  mxikCode: z.string(),
  name: z.string(),
  fullName: z.string().nullable(),
  groupName: z.string().nullable(),
  className: z.string().nullable(),
  positionName: z.string().nullable(),
  subPositionName: z.string().nullable(),
  unitsName: z.string().nullable(),
  packageName: z.string().nullable(),
  usePackage: z.boolean(),
});
export type IkpuSearchItem = z.infer<typeof ikpuSearchItemSchema>;

/** Код упаковки, привязанный к ИКПУ. */
export const ikpuPackageSchema = z.object({
  code: z.string(),
  nameRu: z.string().nullable(),
  nameUz: z.string().nullable(),
  nameLat: z.string().nullable(),
  packageType: z.string().nullable(),
});
export type IkpuPackage = z.infer<typeof ikpuPackageSchema>;

/** Деталь ИКПУ + список упаковок. */
export const ikpuDetailSchema = z.object({
  mxikCode: z.string(),
  nameRu: z.string().nullable(),
  nameUz: z.string().nullable(),
  groupNameRu: z.string().nullable(),
  classNameRu: z.string().nullable(),
  positionNameRu: z.string().nullable(),
  subPositionNameRu: z.string().nullable(),
  isActive: z.boolean(),
  packages: z.array(ikpuPackageSchema),
});
export type IkpuDetail = z.infer<typeof ikpuDetailSchema>;
